// ABOUTME: Migrates guest dashboard data from localStorage to Firestore on sign-up/sign-in.
// ABOUTME: Merges guest items with any existing user items, then clears guest state.

import { guestDashboardService } from "./guestDashboardService";
import { dashboardService } from "./dashboardService";
import { mergeGuestItems } from "../utils/guestMerge";

export interface MigrationSummary {
  /** Guest items that were added to the account. */
  merged: number;
  /** Guest items skipped because the account already had them. */
  duplicates: number;
  /** Guest items skipped because the dashboard would exceed the item cap. */
  overLimit: number;
}

/** Human-readable one-liner for the post-sign-in notice. */
export function describeMigration(summary: MigrationSummary): string {
  const parts: string[] = [];
  parts.push(
    summary.merged === 1
      ? "Moved 1 item from guest mode to your account."
      : `Moved ${summary.merged} items from guest mode to your account.`
  );
  if (summary.duplicates > 0) {
    parts.push(
      `${summary.duplicates} already on your dashboard ${summary.duplicates === 1 ? "was" : "were"} skipped.`
    );
  }
  if (summary.overLimit > 0) {
    parts.push(`${summary.overLimit} didn't fit under the item limit.`);
  }
  return parts.join(" ");
}

export const guestMigrationService = {
  hasGuestData(): boolean {
    const items = guestDashboardService.getItemsForMigration();
    return items.length > 0;
  },

  async migrateToUser(userId: string): Promise<MigrationSummary | null> {
    const guestItems = guestDashboardService.getItemsForMigration();
    if (guestItems.length === 0) return null;

    // Fetch existing user dashboard (may be null for new users)
    const existingItems = (await dashboardService.getDashboard(userId)) || [];

    // Re-index rather than concatenating: guest items keep their own (col,
    // order), so a plain merge collided with existing items at the same slot
    // and the renderer's order-based sort produced an arbitrary arrangement.
    const { items, duplicates, overLimit } = mergeGuestItems(existingItems, guestItems);

    await dashboardService.setDashboard(userId, items);

    // Only clear guest data after successful migration
    guestDashboardService.clearAll();

    return { merged: items.length - existingItems.length, duplicates, overLimit };
  },
};
